// Pure, DOM-free verification state for the webhook form.
//
// Status lives per event. "Send test" verifies a single event; "Save" verifies
// every checked event server-side. Editing the URL, headers, or auth affects
// every event, so those changes invalidate all prior results.

export type VerifyStatus = 'not_tested' | 'testing' | 'passed' | 'failed'

export interface TriggerVerifyResult {
  trigger_type?: string
  success: boolean
  message?: string
}

export interface TriggerState {
  status: VerifyStatus
  message?: string
}

export type VerifyState = Record<string, TriggerState>

export function emptyState(): VerifyState {
  return {}
}

// Edit hydration: trust the per-trigger status the API returned. Anything not
// explicitly "verified" starts as not-yet-tested.
export function stateFromInitialStatuses(
  statuses: Record<string, string> | null | undefined
): VerifyState {
  const state: VerifyState = {}

  if (!statuses) {
    return state
  }

  Object.keys(statuses).forEach(triggerType => {
    const verified = String(statuses[triggerType]).toLowerCase() === 'verified'
    state[triggerType] = { status: verified ? 'passed' : 'not_tested' }
  })

  return state
}

export function markTesting(
  state: VerifyState,
  triggerTypes: string[]
): VerifyState {
  const next = { ...state }
  triggerTypes.forEach(triggerType => {
    next[triggerType] = { status: 'testing' }
  })
  return next
}

export function applyResults(
  state: VerifyState,
  triggerTypes: string[],
  resultsByTrigger: Record<string, TriggerVerifyResult>
): VerifyState {
  const next = { ...state }
  triggerTypes.forEach(triggerType => {
    const result = resultsByTrigger[triggerType]
    next[triggerType] = result
      ? {
          status: result.success ? 'passed' : 'failed',
          message: result.message,
        }
      : { status: 'failed' }
  })
  return next
}

export function clearTriggers(
  state: VerifyState,
  triggerTypes: string[]
): VerifyState {
  const next = { ...state }
  triggerTypes.forEach(triggerType => {
    delete next[triggerType]
  })
  return next
}

// Callers invoke this after a config change so the next verify is authoritative
// rather than trusting stale results.
export function invalidateAll(state: VerifyState): VerifyState {
  const next: VerifyState = {}
  Object.keys(state).forEach(triggerType => {
    next[triggerType] = { status: 'not_tested' }
  })
  return next
}

export function statusFor(
  state: VerifyState,
  triggerType: string
): VerifyStatus {
  return state[triggerType]?.status ?? 'not_tested'
}

export function anyFailed(
  selectedTriggers: string[],
  state: VerifyState
): boolean {
  return selectedTriggers.some(
    triggerType => state[triggerType]?.status === 'failed'
  )
}

export function resultsByTrigger(
  results: TriggerVerifyResult[] | undefined
): Record<string, TriggerVerifyResult> {
  const byTrigger: Record<string, TriggerVerifyResult> = {}

  if (Array.isArray(results)) {
    results.forEach(result => {
      if (result.trigger_type) {
        byTrigger[result.trigger_type] = result
      }
    })
  }

  return byTrigger
}
